import { useCallback } from 'react';

import {
  AgentDelegationTarget,
  InboxEntry,
  LocalCodingAgentRuntime,
} from '@this+that/models';

import { useDoBoxExecutorFlags } from './useDoBoxExecutorFlags';
import { useDelegateInboxEntryToAgent } from '../../services/inboxEntries.service';
import { buildDelegationInputVariables } from '../../utils/delegation/buildDelegationInputVariables';
import { resolveDefaultDelegationTaskType } from '../../utils/delegation/resolveDelegationTaskType';
import { useActiveInboxEntriesSlice } from '../InboxEntries/useActiveInboxEntriesSlice';

export interface DelegateEntryToTargetRequest {
  taskGroupId: string;
  entryId: string;
  target: AgentDelegationTarget;
  planFirst: boolean;
  instructions: string;
  localRuntime: LocalCodingAgentRuntime;
  localModel: string;
  localAgentProfile: string;
  attachmentIds?: string[];
}

export type DelegateEntryToTarget = (
  request: DelegateEntryToTargetRequest,
) => Promise<InboxEntry | null>;

/**
 * The one place that turns a chosen target into a delegateInboxEntryToAgent call and puts the
 * returned entry in the active slice. The panel composer and the card popover both run it, so
 * the task-type and input mapping (resolveDefaultDelegationTaskType, buildDelegationInputVariables)
 * never exists twice. Throws on mutation failure; what follows a success (run view, toast,
 * focus) is the caller's.
 */
export function useDelegateEntryToTarget(): DelegateEntryToTarget {
  const { flags } = useDoBoxExecutorFlags();
  const { updateInboxEntry } = useActiveInboxEntriesSlice();
  const { delegateInboxEntryToAgent } = useDelegateInboxEntryToAgent();
  const isExecuteDelegationEnabled = flags.executeDelegation;

  return useCallback(
    async ({
      taskGroupId,
      entryId,
      target,
      planFirst,
      instructions,
      localRuntime,
      localModel,
      localAgentProfile,
      attachmentIds,
    }) => {
      const taskType = resolveDefaultDelegationTaskType(
        target,
        isExecuteDelegationEnabled,
        planFirst,
      );
      const input = buildDelegationInputVariables({
        target,
        taskType,
        selectedLocalRuntime: localRuntime,
        selectedLocalModel: localModel,
        selectedAgentProfile: localAgentProfile,
        instructions,
        attachmentIds,
      });
      const updatedEntry = await delegateInboxEntryToAgent(
        taskGroupId,
        entryId,
        input,
      );
      if (updatedEntry) {
        updateInboxEntry(updatedEntry);
      }
      return updatedEntry;
    },
    [isExecuteDelegationEnabled, delegateInboxEntryToAgent, updateInboxEntry],
  );
}
